import React from 'react';
import {AbsoluteFill, spring, useCurrentFrame} from 'remotion';
import {COLORS as C, SPRING} from '../../coldcompute/theme';
import timing from './timing.json';

const FONT='ContextInter, sans-serif';
const clamp=(v:number)=>Math.max(0,Math.min(1,v));
export const TOPICS=[
  {chapter:0,label:'Introduction'},
  {chapter:2,label:'What context means'},
  {chapter:3,label:'Follow-up questions'},
  {chapter:4,label:'History vs evidence'},
  {chapter:5,label:'History limits'},
  {chapter:6,label:'Evidence & time'},
  {chapter:7,label:'Access boundaries'},
  {chapter:8,label:'Freshness checks'},
  {chapter:9,label:'Answer caching'},
  {chapter:10,label:'Long-term memory'},
  {chapter:11,label:'Complete answers'},
  {chapter:12,label:'Takeaways'},
].map(t=>({...t,start:Math.round(timing.chapters[t.chapter].start*30)}));

// One frame is the complete source of truth: no playback side effects or
// remembered UI state. A backward seek restores the correct future blur.
export const TopicRail:React.FC=()=>{
  const frame=useCurrentFrame();
  const completed=TOPICS.filter((_,i)=>frame>=(TOPICS[i+1]?.start??9470)).length;
  const current=Math.max(0,TOPICS.reduce((last,t,i)=>frame>=t.start?i:last,0));
  const end=TOPICS[current+1]?.start??9471;
  return <AbsoluteFill style={{left:1536,width:384,background:C.bg2,borderLeft:`1px solid ${C.line}`,fontFamily:FONT,padding:'55px 30px',color:C.text}}>
    <div style={{fontSize:28,fontWeight:750,letterSpacing:-.8}}>Lesson map</div>
    <div style={{fontSize:20,color:C.dim,marginTop:8}}>{completed} of {TOPICS.length} topics covered</div>
    <div style={{marginTop:35,display:'flex',flexDirection:'column',gap:7}}>
      {TOPICS.map((topic,i)=>{
        const done=frame>=(TOPICS[i+1]?.start??9470),active=i===current&&!done,future=frame<topic.start;
        const reveal=future?0:spring({fps:30,frame:frame-topic.start,durationInFrames:16,config:SPRING.snappy});
        return <div key={topic.label} style={{position:'relative',display:'flex',alignItems:'center',gap:13,minHeight:59,padding:'10px 10px',borderRadius:8,borderLeft:`3px solid ${active?C.cyan:'transparent'}`,background:active?C.panel2:'transparent'}}>
          <div style={{width:23,height:23,flexShrink:0,borderRadius:'50%',border:`1.5px solid ${done?C.green:active?C.cyan:C.line}`,background:active?C.cyan:'transparent',display:'flex',alignItems:'center',justifyContent:'center',color:C.green,fontSize:17,fontWeight:800}}>{done?'✓':active?<span style={{width:6,height:6,borderRadius:'50%',background:C.bg}}/>:null}</div>
          <div style={{fontSize:23,lineHeight:1.2,fontWeight:active?720:500,color:active?C.text:done?C.text:C.dim,opacity:future?.38:1,filter:`blur(${future?4.5:4.5*(1-clamp(reveal))}px)`}}>{topic.label}</div>
        </div>;
      })}
    </div>
    <div style={{position:'absolute',left:43,right:40,bottom:65}}>
      <div style={{fontSize:18,color:C.dim,marginBottom:12}}>Current topic</div>
      <div style={{height:4,background:C.line,borderRadius:3,overflow:'hidden'}}><div style={{height:4,background:C.cyan,transform:`scaleX(${clamp((frame-TOPICS[current].start)/(end-TOPICS[current].start))})`,transformOrigin:'left'}}/></div>
    </div>
  </AbsoluteFill>;
};

type Chapter=(typeof timing.chapters)[number];
const PAYOFFS:Record<number,{cue:string;lines:string[];accent:string}>={
  0:{cue:'context',lines:['CONTEXT.'],accent:C.cyan},
  2:{cue:'A bigger desk',lines:['More space.', 'Not better judgment.'],accent:C.amber},
  4:{cue:'does not make the model',lines:['Evidence.', 'Not repetition.'],accent:C.green},
  5:{cue:'useful lesson',lines:['Budget the material.'],accent:C.cyan},
  6:{cue:'Neither proves',lines:['Evidence ≠', 'complete coverage.'],accent:C.amber},
  7:{cue:'does not bypass',lines:['Both access checks', 'must pass.'],accent:C.green},
  8:{cue:'not allowed',lines:['Old plans ≠', 'live features.'],accent:C.amber},
  9:{cue:'useful question',lines:['What? Whose access?', 'For how long?'],accent:C.cyan},
  10:{cue:'Recent conversation',lines:['History. Preferences.', 'Episodes.', 'Different rules.'],accent:C.cyan},
  11:{cue:'confident guess',lines:['Make the gap visible.'],accent:C.amber},
  12:{cue:'When your assistant',lines:['Should it trust', 'that memory here?'],accent:C.cyan},
};
const normalize=(s:string)=>s.toLowerCase().replace(/[^a-z0-9]/g,'');
export const payoffStart=(chapter:Chapter,index:number)=>{
  const beat=PAYOFFS[index];
  if(!beat)return Number.POSITIVE_INFINITY;
  const cue=chapter.cues.find(c=>normalize(c.phrase)===normalize(beat.cue));
  if(!cue)throw new Error(`Missing payoff cue ${chapter.id}: ${beat.cue}`);
  return cue.local;
};

export const PayoffBeat:React.FC<{chapter:Chapter;index:number;time:number}>=({chapter,index,time})=>{
  const beat=PAYOFFS[index],start=payoffStart(chapter,index);
  if(!beat||time<start)return null;
  const f=(time-start)*30;
  return <div style={{position:'absolute',left:85,right:85,top:290,bottom:230,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:16,fontFamily:FONT,textAlign:'center'}}>
    {beat.lines.map((line,i)=>{
      const p=spring({fps:30,frame:Math.max(0,f-i*4),durationInFrames:16,config:SPRING.snappy});
      return <div key={line} style={{fontWeight:800,fontSize:index===0?180:beat.lines.length===3?78:92,lineHeight:1.08,letterSpacing:-3,color:i===beat.lines.length-1?beat.accent:C.text,opacity:clamp(p),transform:`translateY(${26*(1-p)}px) scale(${.95+.05*p})`}}>{line}</div>;
    })}
    <div style={{height:4,width:240,marginTop:20,background:beat.accent,transform:`scaleX(${clamp(f/18)})`}}/>
  </div>;
};

export const RevisedThumbnail:React.FC<{font:React.ReactNode}>=({font})=><AbsoluteFill style={{background:`radial-gradient(ellipse at 78% 46%, #102c38 0%, ${C.bg} 52%, ${C.bg2} 100%)`,fontFamily:FONT,color:C.text}}>
  {font}
  <AbsoluteFill style={{opacity:.045,backgroundImage:'repeating-linear-gradient(0deg,#fff 0 1px,transparent 1px 4px)'}}/>
  <div style={{position:'absolute',left:76,top:66,color:C.amber,fontSize:26,fontWeight:650,letterSpacing:3}}>EAKC / BUILDER NOTEBOOK</div>
  <div style={{position:'absolute',left:74,top:166,fontWeight:850,fontSize:124,letterSpacing:-6,lineHeight:1.02}}>MEMORY<br/>ISN’T<br/><span style={{color:C.cyan}}>ENOUGH.</span></div>
  <div style={{position:'absolute',left:80,bottom:62,fontSize:32,color:C.dim}}>How AI context really works</div>
  <svg viewBox="0 0 1280 720" style={{position:'absolute',inset:0,width:'100%',height:'100%'}}>
    <defs><radialGradient id="context-thumb-glow"><stop stopColor={C.cyan} stopOpacity=".19"/><stop offset="1" stopColor={C.cyan} stopOpacity="0"/></radialGradient></defs>
    <circle cx="1020" cy="345" r="230" fill="url(#context-thumb-glow)"/>
    <path d="M805 210C920 210 860 340 940 340 M1100 355C1200 355 1130 500 1200 500" stroke={C.cyan} strokeWidth="5" fill="none"/>
    <g transform="translate(728 135) rotate(-9 70 75)"><rect width="140" height="150" rx="12" fill={C.panel2} stroke={C.amber} strokeWidth="3"/><path d="M25 38H112M25 70H100M25 103H110" stroke={C.amber} strokeWidth="6" strokeLinecap="round"/><path d="M105 123L125 143M125 123L105 143" stroke={C.amber} strokeWidth="5"/></g>
    <rect x="918" y="260" width="220" height="190" rx="34" fill={C.panel} stroke={C.cyan} strokeWidth="4"/>
    <path d="M963 310L1028 294 1093 327 1067 388 993 398 963 351Z M963 310L1067 388M1028 294L993 398M1093 327L963 351" stroke={C.cyan} strokeWidth="3" opacity=".7" fill="none"/>
    {[[963,310],[1028,294],[1093,327],[1067,388],[993,398],[963,351]].map(([x,y])=><circle key={`${x}-${y}`} cx={x} cy={y} r="9" fill={C.cyan}/>)}
    <g transform="translate(1110 478) rotate(8 55 60)"><rect width="112" height="125" rx="12" fill={C.panel2} stroke={C.green} strokeWidth="3"/><path d="M24 37H86M24 63H81" stroke={C.green} strokeWidth="5" strokeLinecap="round"/><path d="M33 90L48 105 81 77" stroke={C.green} strokeWidth="6" fill="none"/></g>
    <text x="1028" y="491" textAnchor="middle" fill={C.cyan} fontFamily={FONT} fontWeight="700" fontSize="27" letterSpacing="3">CONTEXT</text>
  </svg>
</AbsoluteFill>;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
